import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Button,
  Flex,
  Spinner,
  Empty,
  useColorModeValue,
  useBreakpointValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Portal,
} from '@chakra-ui/react';
import {
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaCreditCard,
  FaWallet,
  FaPlus,
  FaSync,
  FaFilter
} from 'react-icons/fa';
import { FiChevronDown } from 'react-icons/fi';
import { useWalletContext } from '../../context/WalletContext';
import { useAuth } from '../../hooks/useAuth';

const TransactionHistory = React.memo(() => {
  const {
    walletData,
    transactionHistory,
    fetchTransactionHistory,
    formatTransactionType,
    getTransactionColor,
    formatCurrency,
    loading
  } = useWalletContext();

  const { user } = useAuth();

  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [allTransactions, setAllTransactions] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  
  // Responsive values
  const tableSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const showDesktopColumns = useBreakpointValue({ base: false, md: true });
  
  // Optimized: Use dashboard transactions first, then paginate
  useEffect(() => {
    if (walletData?.recent_transactions) {
      setAllTransactions(walletData.recent_transactions);
      console.log('✅ Using optimized dashboard transactions');
    }
  }, [walletData?.recent_transactions]);
  
  // Memoized computed values for performance
  const displayTransactions = useMemo(() => {
    return allTransactions.length > 0 ? allTransactions : transactionHistory;
  }, [allTransactions, transactionHistory]);

  // Check if user is a merchant
  const isMerchant = user?.account_type === 'merchant';

  // Get filter options from linked artists summary (for merchants)
  const filterOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Transactions' }];

    // Check if user is merchant and has linked artists data
    if (isMerchant && walletData?.linked_artists_summary) {
      // Add merchant own transactions option
      options.push({ value: 'merchant', label: 'Merchant Account' });

      // Add each linked artist as option
      const artists = walletData.linked_artists_summary.artists || [];
      artists.forEach(artist => {
        options.push({
          value: `artist:${artist.artist_id}`,
          label: `Artist: ${artist.artist_name}`
        });
      });
    }

    return options;
  }, [isMerchant, walletData?.linked_artists_summary]);

  // Filter transactions based on selected filter
  const filteredTransactions = useMemo(() => {
    if (selectedFilter === 'all') {
      return displayTransactions;
    }

    if (selectedFilter === 'merchant') {
      return displayTransactions.filter(txn => txn.source === 'merchant');
    }

    // Filter by specific artist
    if (selectedFilter.startsWith('artist:')) {
      const artistId = selectedFilter.split(':')[1];
      return displayTransactions.filter(txn => txn.artist_id === artistId);
    }

    return displayTransactions;
  }, [displayTransactions, selectedFilter]);

  // Show filter for merchants with linked artists (based on presence of linked_artists_summary)
  // Use the more reliable check: if merchant has linked_artists_summary with any artists
  const showFilter = isMerchant && walletData?.linked_artists_summary?.total_artists > 0;

  // Theme colors
  const borderColor = 'gray.600';
  const hoverBg = 'gray.700';

  const loadMoreTransactions = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const currentLength = allTransactions.length; // Use all transactions count, not filtered
      const newTransactions = await fetchTransactionHistory(20, currentLength);

      if (newTransactions.length < 20) {
        setHasMore(false);
      }

      // Append new transactions to existing ones
      setAllTransactions(prev => [...prev, ...newTransactions]);
      console.log(`📡 Loaded ${newTransactions.length} additional transactions`);
    } catch (error) {
      console.error('Error loading more transactions:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const getTransactionIcon = (type) => {
    const iconMap = {
      'stripe_deposit': FaArrowDown,
      'purchase': FaArrowUp,
      'earning_transfer': FaArrowDown,
      'stripe_payout': FaArrowUp,
      'transfer_in': FaArrowDown,
      'transfer_out': FaArrowUp
    };
    return iconMap[type] || FaExchangeAlt;
  };

  const getTransactionSign = (type) => {
    const creditTypes = ['stripe_deposit', 'earning_transfer', 'transfer_in'];
    return creditTypes.includes(type) ? '+' : '-';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'completed': { colorScheme: 'green', label: 'Completed' },
      'pending': { colorScheme: 'yellow', label: 'Pending' },
      'processing': { colorScheme: 'blue', label: 'Processing' },
      'failed': { colorScheme: 'red', label: 'Failed' }
    };
    return statusMap[status] || { colorScheme: 'gray', label: status };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatDescription = (description, type) => {
    // Truncate long descriptions for mobile
    const maxLength = showDesktopColumns ? 50 : 25;
    return description.length > maxLength
      ? `${description.substring(0, maxLength)}...`
      : description;
  };

  // Memoized Filter Menu to prevent recreation on every render
  const filterMenu = useMemo(() => {
    if (!showFilter) return null;

    return (
      <Menu placement="bottom-end">
        <MenuButton
          as={Button}
          size="sm"
          variant="ghost"
          rightIcon={<Icon as={FiChevronDown} boxSize={3.5} />}
          leftIcon={<Icon as={FaFilter} boxSize={3.5} />}
          color="primary.100"
          fontWeight="medium"
          _hover={{ bg: 'secondary.700', color: 'primary.100' }}
          _active={{ bg: 'secondary.700' }}
          px={3}
        >
          {filterOptions.find(opt => opt.value === selectedFilter)?.label || 'All Transactions'}
        </MenuButton>
        <Portal>
          <MenuList
            bg="dark.500"
            borderColor="dark.900"
            borderWidth="1px"
            shadow="xl"
            py={2}
            minW="240px"
            zIndex={1500}
            maxH="400px"
            overflowY="auto"
          >
            {filterOptions.map((option, index) => (
              <React.Fragment key={option.value}>
                {index === 1 && <MenuDivider borderColor="dark.900" my={1} />}
                <MenuItem
                  onClick={() => setSelectedFilter(option.value)}
                  bg={selectedFilter === option.value ? 'secondary.700' : 'transparent'}
                  _hover={{ bg: 'secondary.700' }}
                  color="primary.100"
                  fontSize="sm"
                  py={2.5}
                  px={4}
                >
                  <HStack spacing={2} w="full">
                    <Text flex={1}>{option.label}</Text>
                    {selectedFilter === option.value && (
                      <Icon as={FaFilter} boxSize={3} color="primary.400" />
                    )}
                  </HStack>
                </MenuItem>
              </React.Fragment>
            ))}
          </MenuList>
        </Portal>
      </Menu>
    );
  }, [showFilter, filterOptions, selectedFilter]);

  // Determine if we should show empty state
  const showEmptyState = filteredTransactions.length === 0;
  const isLoadingEmpty = loading && filteredTransactions.length === 0;

  return (
    <VStack spacing={4} align="stretch">
      {/* Header - Always visible with consistent structure */}
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Text fontSize="lg" fontWeight="bold" color="white">
            Transaction History
          </Text>
          {!showEmptyState && selectedFilter !== 'all' && (
            <Badge colorScheme="blue" variant="solid" fontSize="xs">
              {filteredTransactions.length} of {displayTransactions.length}
            </Badge>
          )}
        </HStack>
        <HStack spacing={2}>
          {/* Filter Dropdown - Only for merchants with linked artists */}
          {filterMenu}
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Icon as={FaSync} boxSize={3.5} />}
            onClick={() => fetchTransactionHistory(50, 0)}
            isLoading={loading}
            color="primary.100"
            fontWeight="medium"
            _hover={{ bg: 'secondary.700', color: 'primary.100' }}
            _active={{ bg: 'secondary.700' }}
            px={3}
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      {/* Conditional Content: Empty State OR Transaction List */}
      {showEmptyState ? (
        <Box p={8} textAlign="center">
          {isLoadingEmpty ? (
            <Flex justify="center" align="center" h="200px">
              <VStack spacing={4}>
                <Spinner size="lg" color="primary.500" />
                <Text color="gray.400">Loading transactions...</Text>
              </VStack>
            </Flex>
          ) : (
            <VStack spacing={4}>
              <Icon as={FaWallet} boxSize={12} color="gray.500" />
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color="gray.400">
                  {selectedFilter === 'all' ? 'No transactions yet' : 'No transactions found'}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {selectedFilter === 'all'
                    ? 'Your wallet transaction history will appear here'
                    : 'Try selecting a different filter'
                  }
                </Text>
              </VStack>
            </VStack>
          )}
        </Box>
      ) : (
        <>

      {/* Mobile View - Cards */}
      {!showDesktopColumns ? (
        <VStack spacing={3}>
          {filteredTransactions.map((transaction) => {
            const IconComponent = getTransactionIcon(transaction.type);
            const sign = getTransactionSign(transaction.type);
            const color = getTransactionColor(transaction.type);
            
            return (
              <Box
                key={transaction.transaction_id}
                p={4}
                bg="gray.800"
                borderRadius="lg"
                border="1px solid"
                borderColor={borderColor}
                w="100%"
                _hover={{ bg: hoverBg }}
              >
                <HStack justify="space-between" align="start">
                  <HStack spacing={3}>
                    <Icon as={IconComponent} color={`${color}.400`} boxSize={5} />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="bold" color="white">
                        {formatTransactionType(transaction.type)}
                      </Text>
                      {transaction.source_name && (
                        <Text fontSize="xs" color={transaction.source === 'merchant' ? 'purple.400' : 'blue.400'} fontWeight="medium">
                          {transaction.source_name}
                        </Text>
                      )}
                      {!transaction.source_name && transaction.artist_name && (
                        <Text fontSize="xs" color="blue.400" fontWeight="medium">
                          Artist: {transaction.artist_name}
                        </Text>
                      )}
                      <Text fontSize="xs" color="gray.400">
                        {formatDescription(transaction.description, transaction.type)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatDate(transaction.created_at)}
                      </Text>
                    </VStack>
                  </HStack>
                  
                  <VStack align="end" spacing={1}>
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color={`${color}.400`}
                    >
                      {sign}{formatCurrency(transaction.amount)}
                    </Text>
                    <Badge
                      size="sm"
                      colorScheme={getStatusBadge(transaction.status).colorScheme}
                      variant="subtle"
                    >
                      {getStatusBadge(transaction.status).label}
                    </Badge>
                  </VStack>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      ) : (
        /* Desktop View - Table */
        <TableContainer>
          <Table size={tableSize} variant="simple">
            <Thead>
              <Tr>
                <Th color="gray.400" borderColor={borderColor}>Type</Th>
                <Th color="gray.400" borderColor={borderColor}>Description</Th>
                <Th color="gray.400" borderColor={borderColor}>Date</Th>
                <Th color="gray.400" borderColor={borderColor} isNumeric>Amount</Th>
                <Th color="gray.400" borderColor={borderColor}>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredTransactions.map((transaction) => {
                const IconComponent = getTransactionIcon(transaction.type);
                const sign = getTransactionSign(transaction.type);
                const color = getTransactionColor(transaction.type);
                
                return (
                  <Tr 
                    key={transaction.transaction_id} 
                    _hover={{ bg: hoverBg }}
                    borderColor={borderColor}
                  >
                    <Td borderColor={borderColor}>
                      <HStack>
                        <Icon as={IconComponent} color={`${color}.400`} boxSize={4} />
                        <Text fontSize="sm" color="white">
                          {formatTransactionType(transaction.type)}
                        </Text>
                      </HStack>
                    </Td>
                    <Td borderColor={borderColor}>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" color="gray.300" noOfLines={1}>
                          {formatDescription(transaction.description, transaction.type)}
                        </Text>
                        {transaction.source_name && (
                          <Text fontSize="xs" color={transaction.source === 'merchant' ? 'purple.400' : 'blue.400'} fontWeight="medium">
                            {transaction.source_name}
                          </Text>
                        )}
                        {!transaction.source_name && transaction.artist_name && (
                          <Text fontSize="xs" color="blue.400" fontWeight="medium">
                            Artist: {transaction.artist_name}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text fontSize="sm" color="gray.400">
                        {formatDate(transaction.created_at)}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      <Text 
                        fontSize="sm" 
                        fontWeight="bold" 
                        color={`${color}.400`}
                      >
                        {sign}{formatCurrency(transaction.amount)}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge
                        size="sm"
                        colorScheme={getStatusBadge(transaction.status).colorScheme}
                        variant="subtle"
                      >
                        {getStatusBadge(transaction.status).label}
                      </Badge>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

        {/* Load More Button */}
        {hasMore && (
          <Flex justify="center" pt={4}>
            <Button
              variant="outline"
              size="sm"
              onClick={loadMoreTransactions}
              isLoading={loadingMore}
              loadingText="Loading..."
              leftIcon={<FaPlus />}
            >
              Load More
            </Button>
          </Flex>
        )}

        {/* End indicator */}
        {!hasMore && transactionHistory.length > 10 && (
          <Text fontSize="xs" color="gray.500" textAlign="center" pt={4}>
            You've reached the end of your transaction history
          </Text>
        )}
        </>
      )}
    </VStack>
  );
});

export default TransactionHistory;

// Apply similar optimizations to other components